import { useEffect } from 'react'
import { useStores } from 'hooks/useStores'
import { last } from 'lodash'
import { observer } from 'mobx-react'
import { FormProvider, useForm, useFormState, useWatch } from 'react-hook-form'
import { toast } from 'react-toastify'
import { Modal, ModalBody, ModalFooter } from 'reactstrap'
import { uploadFile } from 'API/cms'
import { updateUserById } from 'API/user'
import ButtonGroup from 'components/ButtonGroup'
import Icon from 'components/Icon'
import GroupPermissionInput from 'components/Pages/UserPage/GroupPermissionInput'
import { AuthRoleIdEnum } from 'constants/user'
import { IGroupMemberPermissionId } from 'interfaces/group'
import { IEditEditUserRequest } from 'interfaces/user'
import { getFirstAndLastName } from 'utils/users'
import ChangePasswordSection from './components/ChangePasswordSection'
import GoogleLinkedStatusSection from './components/GoogleLinkedStatusSection'
import LinkGoogleAccountSection from './components/LinkGoogleAccountSection'
import PersonalInformationSection from './components/PersonalInformationSection'
import RoleSelectionSection from './components/RoleSelectionSection'
import { getDefaultValues } from './utils'
import styles from './styles.module.scss'

interface IEditAccountModalProps {
  toggle: () => void
  isOpen: boolean
  userId: number
  isProfile?: boolean
}

const EditAccountModal = (props: IEditAccountModalProps) => {
  const { toggle, isOpen, userId, isProfile } = props
  const title: string = isProfile ? 'My Account' : 'Edit User'
  const { userStore, authStore, groupStore } = useStores()
  const { selectedUserDetail } = userStore
  const isMyAccount = userId === authStore.userDetail.id
  const userDetail = isMyAccount ? userStore.userDetail : selectedUserDetail
  const overrideRoleId = isMyAccount ? (authStore.currentMembership?.roleId ?? undefined) : undefined
  const { groups } = groupStore
  const groupOptions = Array.isArray(groups)
    ? groups.map(group => ({
        label: group.name,
        value: group?.id ?? 0,
        admin: false,
        permissionId: IGroupMemberPermissionId.VIEWER
      }))
    : []

  const defaultValues = getDefaultValues(userDetail, groupOptions, overrideRoleId)
  const methods = useForm({ defaultValues, mode: 'onBlur', reValidateMode: 'onBlur' })
  const { isSubmitting } = useFormState({ control: methods.control })
  const formId = 'MyAccount'
  const authRoleId = useWatch({ control: methods.control, name: 'authRoleId' }) || AuthRoleIdEnum.PLAYER
  const isBasicUser = ((authRoleId?.value ?? AuthRoleIdEnum.PLAYER) as AuthRoleIdEnum) === AuthRoleIdEnum.PLAYER

  useEffect(() => {
    if (isOpen && userId) {
      userStore.getUserDetail(userId, !isMyAccount)
    }
  }, [userId, isOpen])

  useEffect(() => {
    const formValue = getDefaultValues(userDetail, groupOptions, overrideRoleId)
    methods.reset(formValue)
  }, [userDetail])

  useEffect(() => {
    if (!isOpen) {
      methods.reset()
    }
  }, [isOpen])

  async function onSubmit(data: any): Promise<void> {
    let imageName = userDetail?.image
    if (data?.imageFile) {
      try {
        const url = await uploadFile('image', data.imageFile, undefined, authStore.resolvedActiveOrgId ?? undefined)
        imageName = last(url.split('/'))
      } catch {
        toast.error('Something went wrong while uploading the picture.')
      }
    }
    const { firstName, lastName } = getFirstAndLastName(data?.fullName ?? '')

    const updatedUser: IEditEditUserRequest = {
      organizationId: authStore.activeOrgId ?? 0,
      firstName,
      lastName,
      username: data?.username ?? '',
      email: data?.email ?? '',
      authRoleId: data?.authRoleId?.value,
      image: imageName ?? '',
      oldPassword: data?.oldPassword,
      newPassword: data?.newPassword,
      groups: data?.groupPermissions?.map((group: any) => ({
        groupId: group?.value,
        permissionId: group?.permissionId,
        admin: group?.admin
      }))
    }
    if (data?.newPassword && !isProfile) {
      try {
        await userStore.adminChangePassword(userId, data.newPassword)
        delete updatedUser.oldPassword
        delete updatedUser.newPassword
      } catch (error: any) {
        toast.error('Change password failed.')
      }
    }
    if (isProfile) {
      delete updatedUser.authRoleId
      delete updatedUser.groups
    }
    try {
      await updateUserById(userDetail?.id ?? 0, updatedUser)
      methods.reset()
      if (isMyAccount) {
        await userStore.getUserDetail(userId)
        await authStore.getMyUser()
      } else {
        await userStore.getUserDetail(userId, !isMyAccount)
      }
      userStore.refetchUserList()
      toast.success('Account updated successfully.')
      toggle()
    } catch (error: unknown) {
      toast.error('Update failed. Please try again.')
    }
  }

  const canLinkGoogle =
    isProfile === true &&
    isMyAccount &&
    authStore.userDetail.hasLocalPassword === true &&
    authStore.userDetail.googleLinked !== true

  const showGoogleLinkedInProfile =
    isProfile === true && isMyAccount && authStore.userDetail.googleLinked === true

  const hideOldPasswordField =
    isProfile === true &&
    isMyAccount &&
    authStore.userDetail.registeredWithGoogle === true &&
    authStore.userDetail.hasLocalPassword !== true

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" zIndex={10000}>
      <div className={styles.overviewHeader}>
        <h1 className={styles.header}>{title}</h1>
        <button type="button" onClick={toggle} aria-label="Close">
          <Icon icon="multiply" group="unicon" />
        </button>
      </div>

      <ModalBody className={styles.modalWrapper}>
        <FormProvider {...methods}>
          <form id={formId} onSubmit={methods.handleSubmit(onSubmit)}>
            <hr className={styles.separator} />
            <PersonalInformationSection userId={userId} />
            <hr className={styles.separator} />
            {canLinkGoogle && (
              <>
                <LinkGoogleAccountSection />
                <hr className={styles.separator} />
              </>
            )}
            {showGoogleLinkedInProfile && !canLinkGoogle && (
              <>
                <GoogleLinkedStatusSection />
                <hr className={styles.separator} />
              </>
            )}
            {!isProfile && <RoleSelectionSection />}
            {!isProfile && isBasicUser && (
              <GroupPermissionInput
                name="groupPermissions"
                control={methods.control}
                rules={{ required: false }}
                defaultValue={[]}
              />
            )}
            <ChangePasswordSection
              isProfile={isProfile}
              userId={userId}
              hideOldPassword={hideOldPasswordField}
            />
          </form>
        </FormProvider>
      </ModalBody>

      <ModalFooter className={styles.footer}>
        <ButtonGroup formId={formId} toggle={toggle} isSubmitting={isSubmitting} />
      </ModalFooter>
    </Modal>
  )
}

export default observer(EditAccountModal)
